import { Add as AddIcon, Person as PersonIcon, Share as ShareIcon } from '@mui/icons-material'
import { Box, Button, Card, CardContent, Tab, Tabs } from '@mui/material'
import React, { useState } from 'react'
import { useAuthProvider, useDataProvider, useNotify, useRefresh, useTranslate } from 'react-admin'
import PageHeader from '../components/PageHeader'
import { useIsMobile } from '../hooks/useResponsive'
import CreateShareModal from './CreateShareModal'
import MySharedPets from './MySharedPets'
import SharedWithMe from './SharedWithMe'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vet-shares-tabpanel-${index}`}
      aria-labelledby={`vet-shares-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

const VetSharesList: React.FC = () => {
  const translate = useTranslate()
  const isMobile = useIsMobile()
  const [activeTab, setActiveTab] = useState(0)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const dataProvider = useDataProvider()
  const authProvider = useAuthProvider()
  const notify = useNotify()
  const refresh = useRefresh()

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleCreateShare = () => {
    setCreateModalOpen(true)
  }

  const handleCreateModalClose = () => {
    setCreateModalOpen(false)
    refresh() // Refresh data after creating share
  }

  return (
    <Box>
      <PageHeader
        title={translate('pos.pet_shares.title')}
        subtitle={translate('pos.pet_shares.subtitle')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateShare}>
            {translate('pos.pet_shares.create_share')}
          </Button>
        }
      />
      <Card>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {/* Phones: two equal full-width tabs, no icons — the scrollable
                variant cut "Shared With Me" off behind a scroll arrow. */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? 'fullWidth' : 'standard'}
            >
              <Tab
                iconPosition="start"
                icon={isMobile ? undefined : <ShareIcon fontSize="small" />}
                label={translate('pos.pet_shares.tab_my_shared')}
                sx={{ minHeight: 48 }}
              />
              <Tab
                iconPosition="start"
                icon={isMobile ? undefined : <PersonIcon fontSize="small" />}
                label={translate('pos.pet_shares.tab_shared_with_me')}
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            <MySharedPets />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <SharedWithMe />
          </TabPanel>
        </CardContent>
      </Card>

      <CreateShareModal open={createModalOpen} onClose={handleCreateModalClose} />
    </Box>
  )
}

export default VetSharesList
